import * as React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import useThemeStore from "@/shared/stores/useThemeStore";

export type CoffeeCallMode = "Audio" | "Video" | "Both";

type CoffeeCallModeDialogProps = {
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onDone: (mode: CoffeeCallMode) => void;
};

const CoffeeCallModeDialog = ({
  open,
  loading = false,
  onClose,
  onDone,
}: CoffeeCallModeDialogProps) => {
  const [selectedMode, setSelectedMode] =
    React.useState<CoffeeCallMode>("Video");
  const { darkMode } = useThemeStore();

  React.useEffect(() => {
    if (open) {
      setSelectedMode("Video");
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      slotProps={{
        paper: {
          sx: {
            bgcolor: darkMode ? "var(--neutral-800)" : "var(--neutralWhite)",
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          color: darkMode ? "var(--neutral-100)" : "var(--neutral-800)",
        }}
      >
        Modes
      </DialogTitle>
      <DialogContent>
        <Typography
          sx={{
            mb: 1.5,
            fontSize: 14,
            color: darkMode ? "var(--neutral-100)" : "var(--neutralBlack)",
            fontWeight: 600,
            fontFamily: "Lexend",
          }}
        >
          Which mode do you prefer to interact with the users.
        </Typography>

        <RadioGroup
          value={selectedMode}
          onChange={(e) => setSelectedMode(e.target.value as CoffeeCallMode)}
        >
          <FormControlLabel
            value="Audio"
            control={<Radio />}
            label="Audio only"
            sx={{
              color: darkMode ? "var(--neutral-100)" : "var(--neutral-800)",
            }}
          />
          <FormControlLabel
            value="Video"
            control={<Radio />}
            label="Both audio and video"
            sx={{
              color: darkMode ? "var(--neutral-100)" : "var(--neutral-800)",
            }}
          />
          <FormControlLabel
            value="Both"
            control={<Radio />}
            label="Let the user decide"
            sx={{
              color: darkMode ? "var(--neutral-100)" : "var(--neutral-800)",
            }}
          />
        </RadioGroup>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => onDone(selectedMode)}
          disabled={loading}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CoffeeCallModeDialog;
